import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Divider,
  FormControl,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import React from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import * as Yup from "yup";
import { IdentifierMapping } from "../../constants";
import {
  Address,
  Identifier,
  PatientInformation,
} from "../../graphql/baseTypes";
import FormDatePicker from "../Form/FormDatePicker";
import FormInput from "../Form/FormInput";

const validationSchema = Yup.object({
  insuranceNumber: Yup.string().required("Identifier eingeben"),
  identifier: Yup.string().required("Identifizierungstypen eingeben"),
  birthdate: Yup.date().nullable(),
  address: Yup.object()
    .shape({
      street: Yup.string().nullable(),
      number: Yup.string().nullable(),
      city: Yup.string().nullable(),
      zip: Yup.string().nullable(),
    })
    .nullable(),
  patient: Yup.object()
    .shape({
      firstname: Yup.string().required("Vorname eingeben"),
      lastname: Yup.string().required("Vorname eingeben"),
      email: Yup.string().email("Bitte eine valide Email eingeben").nullable(),
    })
    .required(),
});

export interface EmployeeFormValues {
  insuranceNumber: string;
  patient: Omit<PatientInformation, "insuranceNumber">;
  address?: Address;
  identifier: Identifier;
  birthdate?: string | null;
}
export type EmployeeFormFunction = (values: EmployeeFormValues) => Promise<any>;

interface Props {
  onSubmit: EmployeeFormFunction;
  loading?: boolean;
  initi?: EmployeeFormValues;
}

const initialValues: EmployeeFormValues = {
  insuranceNumber: "",
  patient: {
    firstname: "",
    lastname: "",
  },
  identifier: "insurance",
  birthdate: null,
};
const useStyles = makeStyles((theme) => ({
  form: {
    width: "100%", // Fix IE 11 issue.
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 250,
  },
  streetWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  street: {
    marginRight: 30,
  },
}));
const EmployeeForm: React.FC<Props> = ({ onSubmit, loading, initi }) => {
  const classes = useStyles();
  const methods = useForm<EmployeeFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: { ...initialValues, ...initi },
    mode: "all",
  });
  const { handleSubmit, control, getValues } = methods;
  return (
    <FormProvider {...methods}>
      <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="identifier"
          control={control}
          render={({ field: { onChange, value } }) => (
            <FormControl className={classes.formControl}>
              <InputLabel id="select-identifier-label">
                Identifizierungstyp
              </InputLabel>
              <Select
                labelId="select-identifier-label"
                id="select-identifier-select"
                value={value}
                onChange={onChange}
              >
                <MenuItem value={"insurance"}>
                  {IdentifierMapping["insurance"]}
                </MenuItem>
                <MenuItem value={"passport"}>
                  {IdentifierMapping["passport"]}
                </MenuItem>
                <MenuItem value={"identity_card"}>
                  {IdentifierMapping["identity_card"]}
                </MenuItem>
              </Select>
            </FormControl>
          )}
        />
        <FormInput
          variant="outlined"
          margin="normal"
          required
          fullWidth
          name="insuranceNumber"
          label={IdentifierMapping[getValues().identifier]}
        />
        <FormInput
          variant="outlined"
          margin="normal"
          fullWidth
          name="patient.firstname"
          label="Vorname"
        />
        <FormInput
          variant="outlined"
          margin="normal"
          fullWidth
          name="patient.lastname"
          label="Nachname"
        />
        <FormInput
          variant="outlined"
          margin="normal"
          fullWidth
          name="patient.email"
          label="Email"
        />
        <FormDatePicker
          name="birthdate"
          maxDate={new Date()}
          fullWidth
          label="Gebursdatum"
          maxDateMessage={"Kann nicht in der Zukunft liegen"}
        />
        <Typography>Adresse</Typography>
        <Divider />
        <div className={classes.streetWrapper}>
          <FormInput
            variant="outlined"
            margin="normal"
            fullWidth
            name="address.street"
            label="Straße"
            className={classes.street}
          />
          <FormInput
            variant="outlined"
            margin="normal"
            fullWidth
            name="address.number"
            label="Hausnummer"
          />
        </div>
        <FormInput
          variant="outlined"
          margin="normal"
          fullWidth
          name="address.city"
          label="Stadt"
        />
        <FormInput
          variant="outlined"
          margin="normal"
          fullWidth
          name="address.zip"
          label="PLZ"
        />
        <Button
          type="submit"
          fullWidth
          disabled={loading}
          variant="contained"
          color="primary"
          className={classes.submit}
          onClick={handleSubmit(onSubmit)}
        >
          Speichern
        </Button>
      </form>
    </FormProvider>
  );
};

export default EmployeeForm;
